/**
 * Cashflow currency formatter — auto-scales between thousands / lakhs / crores.
 * All values are stored internally in Lakhs (₹L).
 *
 * <  ₹1L        → ₹#,##0
 * ₹1L – ₹100L   → ₹#.#L
 * ≥ ₹100L       → ₹#.##Cr
 * Zero          → en-dash "–"
 */
export function formatMoney(vLakhs, { showZero = false } = {}) {
  if (vLakhs == null || Number.isNaN(vLakhs)) return "–";
  const n = Number(vLakhs);
  if (Math.abs(n) < 0.005 && !showZero) return "–";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 100) {
    const cr = abs / 100;
    return `${sign}₹${cr.toFixed(2)}Cr`;
  }
  if (abs >= 1) {
    return `${sign}₹${abs.toFixed(1)}L`;
  }
  // < 1L: convert lakhs → rupees
  const rupees = Math.round(abs * 100000);
  return `${sign}₹${rupees.toLocaleString("en-IN")}`;
}

/**
 * Cashflow tab standard — always render in Crores with exactly 2 decimals.
 * Use this on the Cashflow ledger, month drill panel, line-item drill and
 * every executive-facing KPI where consistent Cr-formatting is a Chairman
 * requirement. `vLakhs` is expected in ₹ Lakhs (internal storage unit).
 * Zero renders as en-dash unless `showZero` is set.
 */
export function formatCr(vLakhs, { showZero = false, sign = false } = {}) {
  if (vLakhs == null || Number.isNaN(vLakhs)) return "–";
  const n = Number(vLakhs);
  if (Math.abs(n) < 0.005 && !showZero) return "–";
  const cr = n / 100;
  const abs = Math.abs(cr).toFixed(2);
  if (n < 0) return `-₹${abs}Cr`;
  return `${sign && n > 0 ? "+" : ""}₹${abs}Cr`;
}

/** Signed formatter for variance: adds explicit + on positive values. */
export function formatVariance(vLakhs) {
  if (vLakhs == null || Number.isNaN(vLakhs)) return "–";
  const n = Number(vLakhs);
  if (Math.abs(n) < 0.005) return "–";
  const s = formatMoney(vLakhs);
  return n > 0 ? `+${s}` : s;
}

/** Kept as alias for backward compatibility with older imports. */
export const formatLakhs = formatMoney;

export function formatPct(v) {
  if (v == null) return "–";
  return `${(Number(v) * 100).toFixed(1)}%`;
}

/** For month <input type="month"> friendly ISO string, e.g. "2026-07-01" → "2026-07" */
export function monthIsoToInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 7);
}

/** "2026-07" → "2026-07-01" */
export function monthInputToIso(v) {
  if (!v) return "";
  return `${v}-01`;
}

/** ISO month → human label "Jul-26" */
export function monthLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }).replace(" ", "-");
}
